import {
  Badge,
  Banner,
  Button,
  Card,
  DataTable,
  Layout,
  TextStyle,
  TextField,
} from "@shopify/polaris";
import React, { useMemo, useState } from "react";

const ErrorSummary = ({ importHistory }) => {
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [errorSearch, setErrorSearch] = useState("");

  const errorSummary = importHistory.error_summary || {};
  const errors = importHistory.errors || [];

  // Compute filtered/paged errors at component scope to satisfy hooks rules
  const filteredErrors = useMemo(() => {
    if (!errorSearch) return errors;
    const q = errorSearch.toLowerCase();
    return errors.filter((e) => String(e).toLowerCase().includes(q));
  }, [errors, errorSearch]);
  const limit = 20;
  const pagedErrors = showAllErrors ? filteredErrors : filteredErrors.slice(0, limit);

  const getErrorTypeTone = (type) => {
    const toneMap = {
      csv_parsing: "warning",
      graphql_errors: "critical",
      product_creation: "critical",
      product_update: "critical",
      inventory_errors: "warning",
      other: "subdued",
    };
    return toneMap[type] || "subdued";
  };

  const getErrorTypeLabel = (type) => {
    const labelMap = {
      csv_parsing: "CSV Parsing",
      graphql_errors: "GraphQL Errors",
      product_creation: "Product Creation",
      product_update: "Product Update",
      inventory_errors: "Inventory Errors",
      other: "Other Errors",
    };
    return labelMap[type] || type;
  };

  const renderErrorSummary = () => {
    const categoriesTotal = Object.values(errorSummary).reduce(
      (sum, count) => sum + count,
      0
    );
    const listTotal = errors.length;
    const totalErrors = categoriesTotal > 0 ? categoriesTotal : listTotal;

    if (totalErrors === 0) {
      return (
        <Banner status="success" title="No Errors">
          <p>This import completed successfully without any errors.</p>
        </Banner>
      );
    }

    // If categories are populated, show per-category tiles; otherwise show a single total tile
    if (categoriesTotal > 0) {
      return (
        <Layout>
          {Object.entries(errorSummary).map(([type, count]) => {
            if (count === 0) return null;

            return (
              <Layout.Section oneThird key={type}>
                <Card>
                  <Card.Section>
                    <div style={{ textAlign: "center" }}>
                      <TextStyle
                        variation="headingLg"
                        tone={getErrorTypeTone(type)}
                      >
                        {count}
                      </TextStyle>
                      <p>{getErrorTypeLabel(type)}</p>
                    </div>
                  </Card.Section>
                </Card>
              </Layout.Section>
            );
          })}
        </Layout>
      );
    }

    // Fallback: show a single card with total error count based on importHistory.errors
    return (
      <Layout>
        <Layout.Section oneThird>
          <Card>
            <Card.Section>
              <div style={{ textAlign: "center" }}>
                <TextStyle variation="headingLg" tone="critical">
                  {listTotal}
                </TextStyle>
                <p>Total Errors</p>
              </div>
            </Card.Section>
          </Card>
        </Layout.Section>
      </Layout>
    );
  };

  const renderErrorDetails = () => {
    if (errors.length === 0) {
      return null;
    }

    const displayedErrors = pagedErrors;

    const copyToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(errors.join("\n"));
      } catch (e) {
        console.error("Copy failed", e);
      }
    };

    const downloadCsv = () => {
      const header = ["#", "Error"].join(",");
      const rows = errors.map((msg, i) => `${i + 1},"${String(msg).replaceAll('"', '""')}"`);
      const csv = [header, ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "import-errors.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    return (
      <Card title={`Error Details (${filteredErrors.length})`}>
        <Card.Section>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <div style={{ flex: 1, marginRight: 8 }}>
              <TextField
                label="Filter errors"
                labelHidden
                value={errorSearch}
                onChange={setErrorSearch}
                placeholder="Search error text"
                autoComplete="off"
              />
            </div>
            <div style={{ whiteSpace: "nowrap" }}>
              <Button size="slim" onClick={copyToClipboard}>
                Copy
              </Button>
              <Button size="slim" onClick={downloadCsv} style={{ marginLeft: "0.5rem" }}>
                Download CSV
              </Button>
              {filteredErrors.length > limit && (
                <Button
                  size="slim"
                  onClick={() => setShowAllErrors(!showAllErrors)}
                  style={{ marginLeft: "0.5rem" }}
                >
                  {showAllErrors ? "Show Less" : `Show All (${filteredErrors.length})`}
                </Button>
              )}
            </div>
          </div>

          {filteredErrors.length === 0 ? (
            <Banner status="warning" title="No matches">
              <p>No errors match your filter.</p>
            </Banner>
          ) : (
            <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid #f0f1f2", borderRadius: 4 }}>
              {displayedErrors.map((error, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    padding: "8px 12px",
                    borderBottom: "1px solid #f5f6f7",
                    background: index % 2 === 0 ? "#ffffff" : "#fafbfc",
                  }}
                >
                  <div style={{ width: 48 }}>
                    <Badge status="critical">#{index + 1}</Badge>
                  </div>
                  <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{String(error)}</div>
                </div>
              ))}
              {!showAllErrors && filteredErrors.length > limit && (
                <div style={{ textAlign: "center", padding: "8px" }}>
                  <Button size="slim" onClick={() => setShowAllErrors(true)}>
                    Show more ({filteredErrors.length - limit} more)
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card.Section>
      </Card>
    );
  };

  const renderErrorCategories = () => {
    const categories = Object.entries(errorSummary).filter(
      ([_, count]) => count > 0
    );

    if (categories.length === 0) {
      return null;
    }

    const rows = categories.map(([type, count]) => {
      const totalErrors = Object.values(errorSummary).reduce(
        (sum, c) => sum + c,
        0
      );
      const percentage = ((count / totalErrors) * 100).toFixed(1);

      return [
        <Badge status={getErrorTypeTone(type)}>
          {getErrorTypeLabel(type)}
        </Badge>,
        count,
        `${percentage}%`,
      ];
    });

    return (
      <Card title="Error Categories">
        <Card.Section>
          <DataTable
            columnContentTypes={["text", "numeric", "text"]}
            headings={["Category", "Count", "Percentage"]}
            rows={rows}
          />
        </Card.Section>
      </Card>
    );
  };

  return (
    <div>
      <Layout>
        <Layout.Section>{renderErrorSummary()}</Layout.Section>
      </Layout>

      {/* Show category table only when categories exist */}
      {Object.values(errorSummary).some((count) => count > 0) && (
        <Layout>
          <Layout.Section>{renderErrorCategories()}</Layout.Section>
        </Layout>
      )}

      {/* Always show error details when there are errors, even if categories are empty */}
      {errors.length > 0 && (
        <Layout>
          <Layout.Section>{renderErrorDetails()}</Layout.Section>
        </Layout>
      )}
    </div>
  );
};

export default ErrorSummary;
